import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux'

import { Radio, RadioGroup } from '../components/psc/lib/Radio'
import SaveButton from '../components/psc/lib/SaveButton'
import WithdrawAllVersionsModal, { useWithdrawConfirm } from '../components/psc/lib/WithdrawAllVersionsModal'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import CancelButton from '../components/psc/lib/CancelButton'
import DeleteButton from '../components/psc/lib/DeleteButton'
import { PscPhrase } from '../components/psc/psc-phrase';
import NoAccess from '../components/psc/lib/NoAccess'
import FilePaths, { FileButtons } from '../components/psc/lib/FilePaths'
import UserSelect from '../components/psc/lib/UserSelect'
import SingleSelect from '../components/psc/lib/SingleSelect'
import HasTemplate from '../components/psc/lib/HasTemplate'
import TextArea from '../components/psc/lib/TextArea'
import Title from '../components/psc/lib/Title'
import PickDate from '../components/psc/lib/PickDate'
import Applicability from '../components/psc/lib/Applicability'
import CustomFields from '../components/psc/lib/CustomFields'
import Status from '../components/psc/lib/Status'
import HeaderStatusInfo from '../components/psc/lib/HeaderStatusInfo'
import RelatedItems from '../components/psc/lib/RelatedItems'
import Loading from '../components/psc/lib/Loading'

import useAuthentication from '../hooks/useAuthentication';
import useCadmObject from '../hooks/useCadmObject';
import useRecordStatuses from '../hooks/useRecordStatuses';
import useUserManagement from '../hooks/useUserManagement';
import useProjectContents from '../hooks/useProjectContents';
import useGncHistory from '../hooks/useGncHistory';
import useConfiguration from '../hooks/useConfiguration'
import useBrowserTabTitle from '../hooks/useBrowserTabTitle'

const Record = () => {
    const recordsUsingSharePoint = true

    const pscState = useSelector(state => state.pscState.value)
    const project = pscState.project
    const { featureSharePoint } = useConfiguration()
    const location = useLocation()
    const navigate = useNavigate()

    const params = useParams();
    const isNew = params.objID === 'new';
    const projectID = Number(params.projectID);
    const objID = Number(params.objID);

    const { setBrowserTabTitle } = useBrowserTabTitle()
    
    const { currentUser } = useAuthentication();

    const {
        userHasExplicitProjectAccess,
        userHasProjectAccessLevel,
    } = useUserManagement()

    const {
        initCustomFields,
        referenceStructure,
        stdDocumentMap,
        getStdDocument,
        getUnarchivedStdDocuments,
    } = useCadmObject('records')

    const {
        updateRecord,
        deleteRecord,
    } = useProjectContents()
    
    const {
        goBack,
    } = useGncHistory()
    
    // Fields
    const [ id, setID ] = useState(null)
    const [ updatedAt, setUpdatedAt ] = useState(null)
    const [ reference, setReference ] = useState('')
    const [ recordStatusID, setRecordStatusID ] = useState(1)
    const [ originalRecordStatusID, setOriginalRecordStatusID ] = useState(1)

    const [ recordStatusName, setRecordStatusName ] = useState("")
    const [ originalRecordStatusName, setOriginalRecordStatusName ] = useState('')
    const [ ownerUserID, setOwnerUserID ] = useState(0)
    const [ ownerUserName, setOwnerUserName ] = useState("")
    const [ stdDocumentID, setStdDocumentID ] = useState(0) 
    const [ title, setTitle ] = useState("")
    const [ note, setNote ] = useState("")
    const [ createDate, setCreateDate ] = useState(null)
    const [ customFields, setCustomFields ] = useState([])
    const [ projectSystems, setProjectSystems ] = useState([])
    const [ finalVersionFilePath, setFinalVersionFilePath ] = useState("")
    const [ filePaths, setFilePaths ] = useState([])
    const [ downloadOnSave, setDownloadOnSave ] = useState(true)
    const [ showTemplateModal, setShowTemplateModal ] = useState(false)
    const [ hasAccess, setHasAccess ] = useState(true)
    const [ recordModels, setRecordModels ] = useState([])
    const [ relatedItems, setRelatedItems ] = useState([])
    const [ previousVersionRelatedItems, setPreviousVersionRelatedItems ] = useState([])
    const [ isUsed, setIsUsed ] = useState(false)

    // Form controls
    const [ error, setError ] = useState(false)
    const [ submitActive, setSubmitActive ] = useState(false)
    const [ submitSpinner, setSubmitSpinner ] = useState(false)
    const [ hasLoaded, setHasLoaded ] = useState(isNew ? true: false)
    const formRef = useRef(null)
    const saveButtonRef = useRef(null)

    const {
        recordStatuses,
    } = useRecordStatuses()

    const stdDocument = useMemo(() => {
        return getStdDocument(stdDocumentID)
    }, [stdDocumentMap, stdDocumentID]);
    
    useEffect(() => {
        const status = recordStatuses.find((element) => element.id === recordStatusID)
        setRecordStatusName(status ? status.name : "")
    }, [recordStatuses, recordStatusID])
    
    useEffect(() => {
        const status = recordStatuses.find((element) => element.id === originalRecordStatusID)
        setOriginalRecordStatusName(status ? status.name : "")
    }, [recordStatuses, originalRecordStatusID])
    
    const finalVersionFilePathInUse = useMemo(() => {
        if (recordStatusName === "Withdrawn") {
            return false;
        }
        return true
    }, [recordStatusName]);
        
    const filePathsInUse = useMemo(() => {
        if (recordStatusName === "Withdrawn") {
            return false;
        }
        return true;
    }, [featureSharePoint, recordStatusName]);
    
    useEffect(() => {
        if( ! isNew || ! initCustomFields ) {
            return
        }
        setCustomFields(initCustomFields)
    }, [initCustomFields])
    
    useEffect(() => {
        if (isNew) {
            setOwnerUserID(currentUser.id)
            return
        }
        
        // It's not new, so don't default to Download on Save
        setDownloadOnSave(false)
        fetchObject()
    }, [objID, isNew])

    useEffect(() => {
        if( isNew ) {
            getReference()
        }
    }, [stdDocumentID, isNew])

    useEffect(() => {
        if( isNew ) {
            setBrowserTabTitle('New Record')
            return
        }
        if( reference || title ) {
            setBrowserTabTitle(`${reference} ${title}`.trim())
        }
    }, [isNew, reference, title, setBrowserTabTitle])

    const fetchObject = () => {
        fetch('/v0/records/'+objID, { credentials: 'same-origin' })
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(json => {
                if(json.error) {
                    throw new Error(json.error)
                }

                const result = json.result
                setID(result.id)
                setReference(result.reference)
                setTitle(result.title)
                setNote(result.note)
                setOwnerUserID(result.owner_user_id)
                setStdDocumentID(result.std_document_id)
                setRecordStatusID(result.record_status_id)
                setCreateDate(result.create_date)
                setFilePaths(result.file_paths || [])
                setFinalVersionFilePath(result.final_version_file_path || '')
                setCustomFields(result.custom_fields || [])
                setProjectSystems(result.project_systems || [])
                setRelatedItems(result.related_items || [])
                setPreviousVersionRelatedItems(result.previous_version_related_items || [])
                setRecordModels(result.record_models)
                setUpdatedAt(result.updated_at)
                setIsUsed(result.is_used)
                
                setOriginalRecordStatusID(result.record_status_id)
                setHasLoaded(true)
            })
            .catch(err => {
                console.log('Record fetch error', err)
                setHasAccess(false)
            })
    }

    const toBackend = () => {
        const data = {
            reference,
            title,
            note,
            project_id: projectID,
            owner_user_id: ownerUserID,
            record_status_id: recordStatusID,
            std_document_id: stdDocumentID,
            create_date: createDate,
            project_systems: projectSystems,
            custom_fields: customFields,
            final_version_file_path: finalVersionFilePath,
            file_paths: filePaths,
            record_models: recordModels,
            related_items: relatedItems,
            updated_at: updatedAt,
        }

        // If status is Withdrawn, clear file paths
        if(recordStatusID === 3) {
            data.final_version_file_path = ''
            data.file_paths = []
        }
        return data
    }

    const getReference = () => {
        if( typeof stdDocumentID === 'undefined' || stdDocumentID === null || stdDocumentID === 0 ) {
            return
        }
        fetch('/v0/records/reference', {
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            method: 'POST',
            body: JSON.stringify({
                project_id: parseInt(projectID, 10),
                std_document_id: stdDocumentID
            })
        }).then(function(response) {
            return response.json()
        }).then(function(json) {
            setReference(json.result.reference)
        })
        return;
    }

    const validateBeforeSubmit = () => {
        let errors = []
        if (!stdDocumentID) {
            errors.push('Type is required')
        }

        if (!title || /^\s+$/.test(title)) {
            errors.push('Title is required')
        }

        if (!ownerUserID) {
            errors.push('Owner is required')
        }
        
        if (recordStatusID === 2) { // Filed
            if (!createDate) {
                errors.push('Date is required')
            }
            if (finalVersionFilePath === '') {
                errors.push('File Location is required')
            }
        }

        if (errors.length > 0) {
            setError(errors)
            return false
        }
        return true
    }

    const onTemplateModalClose = () => {
        goBack()
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setError([ props.message ])
    }
    
    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }

        if(submitActive || !validateBeforeSubmit()) {
            return null
        }

        setSubmitActive(true)
        setSubmitSpinner(true)

        let submit = {
            objType: 'records',
            objID: objID,
            data: toBackend(),
            successCallback: function(json) {
                updateRecord(json.result)
            }
        }
        // Only if still status = Reserved, and there is a template
        if( status === "Reserved" && downloadOnSave && stdDocument.template_id > 0 ) {
            if (recordsUsingSharePoint && featureSharePoint && project.is_using_sharepoint_integration) {
                // SharePoint upload, don't do modal, go to different page
                // this modal is in TemplateModal component. open it after save
                submit.successCallback = function(json) {
                    updateRecord(json.result)
                    setSubmitActive(false)
                }
                submit.navigateOverride = function(json) {
                    navigate('/portfolio/'+projectID+'/records/'+json.result.id+'/sharepoint_upload')
                }

            }
            else {
                // this modal is in TemplateModal component. open it after save
                submit.successCallback = function(json) {
                    updateRecord(json.result)
                    setShowTemplateModal(true)
                }
                submit.navigateOverride = function(json) {
                    // Nothing! Stay on this page
                }
            }
        }
        return submit
    }

    const withdrawConfirm = useWithdrawConfirm({
        originalStatusName: originalRecordStatusName,
        statusName: recordStatusName,
        formRef,
        saveButtonRef,
        handleSubmit,
    })
    const { withdrawAllVersions, handleSaveClick } = withdrawConfirm

    const statusIsDisabled = (status) => {
        if (!userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')) {
            return true
        }
        return false
    }

    const templateData = () => {
        const data = {
            note: note,
            owner_user_id: ownerUserID,
            reference: reference,
            reference_without_version: reference,
            title: title,
            create_date: createDate,

            project: project,
            std_document: { name: stdDocument.name },
        }
        return data
    }

    const status = recordStatusName

    const isFieldsDisabled = useMemo(() => {
        if (id && originalRecordStatusID !== 1) {
            return true
        }
        return !userHasProjectAccessLevel(currentUser, projectID, 'MEMBER')
    }, [id, recordStatusID, currentUser, project])

    const isNotesDisabled = useMemo(() => {
        return !userHasProjectAccessLevel(currentUser, projectID, 'MEMBER')
    }, [currentUser, project])

    const deleteButton = useMemo(() => {
        if (id && userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER') && ! isUsed) {
            return (
                <DeleteButton 
                    url={`/v0/records/${objID}`}
                    setError={setError}
                    callback={() => deleteRecord(objID)}
                    extraText="Deleting this item will remove it completely from GNC Go, including anywhere it is used or referenced - in Reports, Deliveries and elsewhere. (To keep any references to this item in GNC Go you can Withdraw it instead.)"
                />
            )
        }
        return null
    }, [id, currentUser, project, objID, isUsed])

    const requiredForReservedIndicator = ((recordStatusID === 1) // Reserved 
                                       && !isFieldsDisabled && userHasProjectAccessLevel(currentUser, projectID, 'MEMBER'))
                                       ? (<label className="form-required-star">&nbsp;*</label>)
                                       : '';

    const requiredForLiveIndicator = ((recordStatusID === 2)  // Filed
                                   && !isFieldsDisabled && userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER'))
                                   ? (<label className="form-required-star">&nbsp;*</label>)
                                   : '';
    
    const requiredFileIndicator = (recordStatusID === 2)  // Filed
                               && userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER');
    
    const applicabilityDisabled = userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER")
                                ? false
                                : isFieldsDisabled

    if (!hasAccess) {
        return <NoAccess />
    }

    if (!hasLoaded) {
        return <Loading />
    }
    
    // Need to refresh "status is disabled" data -- it depends on data from the backend
    recordStatuses.forEach((element) => element.disabled = statusIsDisabled(element))
    const originalStatus = originalRecordStatusName

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <HeaderStatusInfo
                                type="Record"
                                id={id}
                                status={originalRecordStatusName}
                            />
                            
                            <Title
                                type="Record"
                                reference={reference}
                                title={title}
                                id={id}
                                showLatest={false}
                            />
                            
                            <FileButtons
                                finalVersionFilePath={finalVersionFilePath}
                                visible={originalStatus === "Filed"}
                                entityType="Record"
                            />
                        </div>
                        <div className="ibox-content">
                            <form ref={formRef} className="form-horizontal" onSubmit={(event) => handleSubmit(event)}>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('LABEL_STD_DOCUMENT')}
                                        {requiredForReservedIndicator}{requiredForLiveIndicator}
                                    </label>

                                    <div className="col-sm-8">
                                        { !isNew
                                          ? <input 
                                                className="form-control" 
                                                type="text" 
                                                disabled={true}
                                                value={stdDocument.name} 
                                            />
                                          : <SingleSelect
                                                value={stdDocumentID}
                                                onChange={(value) => setStdDocumentID(value.std_document_id)}
                                                options={getUnarchivedStdDocuments()}
                                                required
                                            />
                                        }
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('LABEL_REFERENCE')}</label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="reference"
                                               value={reference}
                                               disabled={true}
                                        />
                                        <span className="help-block m-b-none m-t-none">
                                            <small className="form-info-text" > {PscPhrase('RECORD_CREATED_AUTOMATICALLY')}</small>
                                        </span>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('LABEL_TITLE')}
                                        {requiredForReservedIndicator}{requiredForLiveIndicator}
                                    </label>

                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="title" value={title} onChange={(event) => setTitle(event.target.value)} disabled={isFieldsDisabled} required />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('RECORD_LABEL_NOTE')}</label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" name="note" value={note} onChange={(event) => setNote(event.target.value)} disabled={isNotesDisabled} />
                                    </div>
                                </div>

                                <CustomFields
                                    customFields={customFields}
                                    setCustomFields={setCustomFields}
                                    cadmObjectType='records'
                                    disabled={isNotesDisabled}
                                />

                                <UserSelect
                                    label={PscPhrase('RECORD_LABEL_OWNER')}
                                    required={true}
                                    value={ownerUserID}
                                    onChange={setOwnerUserID}
                                    disabled={isFieldsDisabled}
                                    projectId={projectID}
                                />

                                <div className="hr-line-dashed"></div>

                                <Applicability
                                    projectId={projectID}
                                    projectType={project.project_type.name}
                                    options={project.project_systems}
                                    value={recordModels}
                                    onChange={(value) => setRecordModels(value)}
                                    disabled={applicabilityDisabled}
                                    onlyAllowOneSystem={recordsUsingSharePoint && featureSharePoint && project.is_using_sharepoint_integration}
                                />

                                <Status
                                    value={recordStatusID}
                                    onChange={(value) => setRecordStatusID(value) }
                                    statuses={recordStatuses}
                                />

                                <div className="form-group">
                                  <label className="col-sm-2 control-label">{PscPhrase('RECORD_LABEL_CREATE_DATE')}
                                    {requiredForLiveIndicator}
                                  </label>

                                  <div className="col-sm-8">
                                    <PickDate
                                        value={createDate}
                                        onChange={(date) => setCreateDate(date)}
                                        disabled={isFieldsDisabled}
                                    />
                                  </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                <HasTemplate
                                    entityType="Record"
                                    templateData={templateData}
                                    templateID={stdDocument.template_id}
                                    filename={reference + " " + title}
                                    disabled={! userHasProjectAccessLevel(currentUser,projectID, "MEMBER")}
                                    visible={status === "Reserved" ? true : false}
                                    downloadOnSave={downloadOnSave}
                                    onDownloadOnSaveChange={(value) => setDownloadOnSave(value)}
                                    showModal={showTemplateModal}
                                    onModalClose={onTemplateModalClose}
                                    showSharePointInfo={recordsUsingSharePoint && featureSharePoint && project.is_using_sharepoint_integration}
                                />

                                <FilePaths
                                    disabled={!userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER")}
                                    filePaths={filePaths}
                                    finalVersionFilePath={finalVersionFilePath}
                                    setFilePaths={(value) => setFilePaths(value)}
                                    setFinalVersionFilePath={(value) => setFinalVersionFilePath(value)}
                                    entityType="Record"
                                    filePathsInUse={filePathsInUse}
                                    finalVersionFilePathInUse={finalVersionFilePathInUse}
                                    finalVersionLabel="Filed"
                                    showRequired={requiredFileIndicator}
                                    buttonClass={originalStatus === "Superseded" ? "btn-warning" : ""}
                                    displayOriginal={false}
                                />

                                <div className="hr-line-dashed"></div>

                                <RelatedItems
                                    object_type="records"
                                    object_reference_without_version={reference}
                                    project_id={projectID}
                                    project_type={project.project_type.name}
                                    related_items={relatedItems}
                                    previous_version_related_items={previousVersionRelatedItems}
                                    disabled={ ! userHasProjectAccessLevel(currentUser,projectID, "MEMBER") }
                                    onChange={(value) => setRelatedItems(value)}
                                />

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />

                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={userHasProjectAccessLevel(currentUser,projectID, "MEMBER")}
                                            spinnerActive={submitSpinner}
                                            label='Save Changes'
                                            onClick={handleSaveClick}
                                            onError={handleSubmitError}
                                            ref={saveButtonRef}
                                        />

                                        {deleteButton}

                                    </div>
                                </div>
                            </form>

                            <WithdrawAllVersionsModal
                                entityLabel="Record"
                                disableAllVersionsCheckbox={true}
                                {...withdrawConfirm}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Record
